import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { lazy, Suspense, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import {
  Breadcrumb,
  Button,
  getInput,
  MenuButton,
  Modal,
} from "../../../components/ui";
import {
  CREATE_INSTANCE_JOB as createInstanceJobMutation,
  DELETE_INSTANCE,
  MAKE_AVAILABLE_INSTANCE as makeAvailableInstanceMutation,
  UNCONSIGN_INSTANCE as unconsignInstanceMutation,
  UNDO_STATUS_INSTANCE as undoStatusInstanceMutation,
  UPDATE_INSTANCE as updateInstanceMutation,
} from "../../../graphql/mutation/Instance";
import { FETCH_INSTANCE as fetchInstance } from "../../../graphql/query/Instance";
import { FETCH_LIST as fetchList } from "../../../graphql/query/List";
import { FETCH_RESERVATION_INSTANCES_V2 as fetchReservationInstancesV2 } from "../../../graphql/query/ReservationInstance";
import { FETCH_STATUS } from "../../../graphql/query/Status";
import useUser from "../../../hooks/useUser";
import routes from "../pages/Wizard/routes";
import AddInstanceImagesToSelectList from "./Actions/AddInstanceImagesToSelectList";
import AddInstancePrimaryImageToSelectList from "./Actions/AddInstancePrimaryImageToSelectList";
import AddInstanceToSelectList from "./Actions/AddInstanceToSelectList";
import AddToOfferSelectList from "./Actions/AddToOfferSelectList";
import AdhocReportBody from "./Actions/AdhocReportBody";
import CopyDescriptionBody from "./Actions/CopyDescriptionBody";
import CopyWarehouseDescription from "./Actions/CopyWarehouseDescription";
import { ExportInstancesBody } from "./Actions/ExportInstances";
import exportModule from "./Actions/ExportModule";
import StorageEstimate from "./Actions/StorageEstimate";
import InstanceSearch from "./InstanceSearch";
import { instanceFilterStore } from "./useInstanceFilters";
import useInstanceNavigation from "./useInstanceNavigation";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";
import { useEntities } from "../../../hooks/useEntities";

const ChangeEditionNumber = lazy(() => import("./ChangeEditionNumber"));

const IGNORED_SEARCH_KEYS = new Set([
  "limit",
  "offset",
  "sort1",
  "sort2",
  "sort3",
  "tab",
]);

const normaliseSearchValue = value => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ");
  }
  return value;
};
const NAVIGATION_CONTEXT_TOOLTIP =
  "A search must be performed to provide previous and next context.";

const collectSearchSummaryItems = ({ input = {}, prefix = "" } = {}) => {
  if (!input || typeof input !== "object") return [];

  return Object.entries(input).flatMap(([rawKey, rawValue]) => {
    const key = String(rawKey);
    if (!key || IGNORED_SEARCH_KEYS.has(key)) return [];

    const qualifiedKey = prefix ? `${prefix}.${key}` : key;
    if (rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)) {
      if ("value" in rawValue) {
        const value = normaliseSearchValue(rawValue.value);
        if (value === undefined || value === null || value === "") return [];
        return [`${qualifiedKey}: ${value}`];
      }
      return collectSearchSummaryItems({
        input: rawValue,
        prefix: qualifiedKey,
      });
    }

    const value = normaliseSearchValue(rawValue);
    if (value === undefined || value === null || value === "") return [];
    return [`${qualifiedKey}: ${value}`];
  });
};

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const search = instanceFilterStore.get();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const instanceFragmentProps = {
    edition: { artwork: { artists: {}, imagesSummary: { imgT: {} } } },
    status: {},
  };

  const pathnames = location?.pathname.split("/");
  const instanceId = pathnames?.length > 2 ? pathnames[2] : null; // get id from url
  const instanceIdNumber = instanceId ? Number(instanceId) : null;

  // Minimal query for PrimaryNav - only fetch what we need for navigation and actions
  const { data: { instance } = {} } = useQuery(
    fetchInstance({
      artworkId: true, // For "Go To Artwork" navigation
      description: true, // For copy description modal
      status: true,
      statusId: true, // For status-based action visibility,
      edition: { artwork: { images: { archiveId: true } } },
    }),
    {
      skip: !instanceId,
      variables: { id: +instanceId },
    },
  );

  const { data: { status: instanceStatus = [] } = {} } = useQuery(FETCH_STATUS);
  const [
    deleteInstance,
    {
      data: { deleteInstance: { error: deleteInstanceError } = {} } = {},
      loading: deleteInstanceLoading,
      reset,
    },
  ] = useMutation(DELETE_INSTANCE);
  const [
    updateInstance,
    {
      data: { updateInstance: { error: updateInstanceError } = {} } = {},
      loading: updateInstanceLoading,
      reset: resetUpdateInstance,
    },
  ] = useMutation(updateInstanceMutation(instanceFragmentProps));
  const [
    unconsignInstance,
    {
      data: { unconsignInstance: { error: unconsignInstanceError } = {} } = {},
      loading: unconsignInstanceLoading,
      reset: resetUnconsignInstance,
    },
  ] = useMutation(unconsignInstanceMutation(instanceFragmentProps));
  const [
    undoStatusInstance,
    {
      data: {
        undoStatusInstance: { error: undoStatusInstanceError } = {},
      } = {},
      loading: undoStatusInstanceLoading,
      reset: resetUndoStatusInstance,
    },
  ] = useMutation(undoStatusInstanceMutation(instanceFragmentProps));
  const [
    makeAvailableInstance,
    {
      data: {
        makeAvailableInstance: { error: makeAvailableInstanceError } = {},
      } = {},
      loading: makeAvailableInstanceLoading,
      reset: resetMakeAvailableInstance,
    },
  ] = useMutation(makeAvailableInstanceMutation(instanceFragmentProps), {
    refetchQueries: [
      fetchReservationInstancesV2({
        reservation: { reservationUser: {} },
      }),
    ],
  });
  const [
    createInstanceJob,
    {
      data: { createInstanceJob: { error: createInstanceJobError } = {} } = {},
      loading: createInstanceJobLoading,
      reset: resetCreateInstanceJob,
    },
  ] = useMutation(createInstanceJobMutation());

  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const imageListIdParam = queryParams.get("imageListId");
  const workingListIdParam = queryParams.get("workingListId");
  const imageWorkingListIdParam = queryParams.get("imageWorkingListId");
  const listId = listIdParam ? +listIdParam : null;
  const imageListId = imageListIdParam ? +imageListIdParam : null;
  const workingListId = workingListIdParam ? +workingListIdParam : null;
  const imageWorkingListId = imageWorkingListIdParam
    ? +imageWorkingListIdParam
    : null;
  const listContextQuery = [
    listIdParam ? `listId=${listIdParam}` : null,
    imageListIdParam ? `imageListId=${imageListIdParam}` : null,
    queryParams.get("workingListId")
      ? `workingListId=${queryParams.get("workingListId")}`
      : null,
    queryParams.get("imageWorkingListId")
      ? `imageWorkingListId=${queryParams.get("imageWorkingListId")}`
      : null,
  ]
    .filter(Boolean)
    .join("&");
  const buildInstanceListUrl = nextStatus =>
    `/instance?status=${nextStatus}${listContextQuery ? `&${listContextQuery}` : ""}`;
  const buildInstanceDetailsUrl = instanceId =>
    `${instanceId}/details?status=${status}${listContextQuery ? `&${listContextQuery}` : ""}`;

  const { ids, select, disabled } = useMemo(() => {
    let select,
      ids = [];

    if (status === "imageSelectListWorking") {
      // we have artwork's reference to its instance when viewing images
      ids = Object.entries(formik.values.selectedImages?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.artwork?.instanceReference;
        },
      );
      select = formik?.values?.selectedImages?.select;
    } else {
      ids = Object.entries(formik.values.selected?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.id || value?.id;
        },
      );
      select = formik?.values?.selected?.select;
    }

    return {
      ids,
      select,
      disabled: ids?.length === 0,
    };
  }, [formik.values, status]);
  const listContext = useMemo(
    () => ({
      ...(listIdParam ? { listId: listIdParam } : {}),
      ...(imageListIdParam ? { imageListId: imageListIdParam } : {}),
      ...(workingListIdParam ? { workingListId: workingListIdParam } : {}),
      ...(imageWorkingListIdParam
        ? { imageWorkingListId: imageWorkingListIdParam }
        : {}),
    }),
    [imageListIdParam, imageWorkingListIdParam, listIdParam, workingListIdParam],
  );

  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigationSnapshot,
    navigateToNextInstance,
    navigateToPreviousInstance,
    showInstanceNavigationControls,
  } = useInstanceNavigation({
    activeSubPage: route?.href || "details",
    instanceIdNumber,
    listContext,
    listContextQuery,
    navigate,
    status,
  });

  // Fetch list names when viewing saved lists (only if listId/imageListId is explicitly in URL and is a valid number)
  const { data: { list: savedList } = {} } = useQuery(
    fetchList(),
    {
      variables: { id: listId },
      skip: !listIdParam || !listId || isNaN(listId) || listId <= 0, // Only fetch if listId is explicitly in URL and valid
      errorPolicy: "ignore", // Ignore errors if list doesn't exist
    }
  );

  const { data: { list: savedImageList } = {} } = useQuery(
    fetchList(),
    {
      variables: { id: imageListId },
      skip: !imageListIdParam || !imageListId || isNaN(imageListId) || imageListId <= 0, // Only fetch if imageListId is explicitly in URL and valid
      errorPolicy: "ignore", // Ignore errors if list doesn't exist
    }
  );

  const statusMap = {
    all: "All Instances",
    searched: "Searched Instances",
    selectListWorking: listIdParam && savedList?.name
      ? `Select List (${savedList.name})`
      : "Select List (Working)",
    imageSelectListWorking: imageListIdParam && savedImageList?.name
      ? `Image Select List (${savedImageList.name})`
      : "Image Select List (Working)",
  };
  const label = statusMap[status] || "All Instances";
  const searchSummaryItems = useMemo(
    () =>
      collectSearchSummaryItems({
        input: navigationSnapshot?.currentSearchParams || {},
      }),
    [navigationSnapshot?.currentSearchParams],
  );
  const isSearchBreadcrumbContext =
    status === "all" && searchSummaryItems.length > 0;
  const breadcrumbRoot = isSearchBreadcrumbContext
    ? {
      label: "Instances Search",
      title: searchSummaryItems.join(" | "),
    }
    : { label };

  // prepare steps for breadcrumbs
  const steps = [
    { ...breadcrumbRoot, onClick: () => navigate(buildInstanceListUrl(status)) },
    ...(instance
      ? [
        {
          label: instance.name,
          onClick: () => navigate(buildInstanceDetailsUrl(instance.id)),
        },
        {
          label: route?.name,
          onClick: () => navigate(`${instance.id}/${route?.href}`),
        },
      ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const deleteModalProps = {
    title: deleteInstanceError ? "Error" : "Delete Instance?",
    scale: "sm",
    description: deleteInstanceError
      ? deleteInstanceError
      : `Are you sure you wish to delete this instance? This action cannot be undone`,
    closeOnBackdrop: true,
    onClose: () => reset(),
    body: ({ closeModal }) => {
      const handleDelete = () => {
        deleteInstance({
          variables: {
            input: { id: instance?.id },
          },
          update: (cache, { data }) => {
            const { deleteInstance: { success } = {} } = data || {};
            if (success) {
              closeModal();
              cache.evict({
                id: cache.identify({
                  __typename: "Instance",
                  id: instance?.id,
                }),
              });
              navigate(buildInstanceListUrl(status));
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={deleteInstanceError ? "Close" : "Cancel"}
            action={deleteInstanceError ? "primary" : "default"}
            onClick={() => {
              closeModal?.();
            }}
          />
          {!deleteInstanceError && (
            <Button
              label={deleteInstanceLoading ? "Deleting" : "Delete"}
              disabled={deleteInstanceLoading}
              onClick={handleDelete}
            />
          )}
        </div>
      );
    },
  };

  const createJobModalProps = {
    title: createInstanceJobError ? "Error" : "Create Job",
    scale: "sm",
    description: createInstanceJobError
      ? createInstanceJobError
      : "Are you sure you wish to create job for this instance?",
    closeOnBackdrop: true,
    instance,
    body: ({ closeModal }) => {
      const handleConfirm = () => {
        createInstanceJob({
          variables: {
            id: instance?.id,
          },
          update: (cache, { data }) => {
            const { createInstanceJob: { job, success } = {} } = data || {};
            if (success) {
              closeModal();
              navigate(`/jobs/${job.id}/details`);
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={createInstanceJobError ? "Close" : "Cancel"}
            action={createInstanceJobError ? "primary" : "default"}
            onClick={() => {
              resetCreateInstanceJob();
              closeModal?.();
            }}
          />
          {!createInstanceJobError && (
            <Button
              label={createInstanceJobLoading ? "Creating" : "create"}
              disabled={createInstanceJobLoading}
              onClick={handleConfirm}
            />
          )}
        </div>
      );
    },
  };

  const setStatusModalProps = ({ status }) => ({
    title: updateInstanceError
      ? "Error"
      : `Set Instance Status to ${status?.name?.toUpperCase()}?`,
    scale: "sm",
    description: updateInstanceError
      ? updateInstanceError
      : `Are you sure you wish to change this instance status to "${status?.name}"?`,
    closeOnBackdrop: true,
    onClose: () => resetUpdateInstance(),
    body: ({ closeModal }) => {
      const handleUpdate = () => {
        updateInstance({
          variables: {
            input: { id: instance?.id, statusId: status?.id },
          },
          update: (cache, { data }) => {
            const { updateInstance: { success } = {} } = data || {};
            if (success) {
              closeModal();
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={updateInstanceError ? "Close" : "Cancel"}
            action={updateInstanceError ? "primary" : "default"}
            onClick={() => {
              closeModal?.();
            }}
          />
          {!updateInstanceError && (
            <Button
              label={updateInstanceLoading ? "Updating" : "Update"}
              disabled={updateInstanceLoading}
              onClick={handleUpdate}
            />
          )}
        </div>
      );
    },
  });

  const setMakeAvailableModalProps = ids => ({
    title: makeAvailableInstanceError
      ? `Error: ${makeAvailableInstanceError}`
      : `Make Available?`,
    scale: "sm",
    closeOnBackdrop: true,
    onClose: () => resetMakeAvailableInstance(),
    body: ({ closeModal }) => {
      const handleUpdate = () => {
        const input = {
          description: formik?.values?.description,
          entityId: formik?.values?.entityId,
          ...(ids && ids.length > 0 && { ids: ids }),
          ...(ids?.length < 1 && { id: instance?.id }),
        };
        makeAvailableInstance({
          variables: {
            input,
          },
          update: (cache, { data }) => {
            const { makeAvailableInstance: { success, reservationId } = {} } =
              data || {};
            if (success) {
              closeModal();
              formik?.resetForm();
              if (reservationId) {
                navigate(`/reservations/${reservationId}/details`);
              }
            }
          },
        });
      };

      // eslint-disable-next-line
      const formik = useFormik({
        initialValues: {},
        validationSchema: Yup.object({
          entityId: Yup.string().required("Required"),
          description: Yup.string().required("Required"),
        }),
        onSubmit: handleUpdate,
      });

      // eslint-disable-next-line
      const { entities } = useEntities();
      const inputs = [
        {
          label: "Entity",
          name: "entityId",
          type: "multi-select",
          options:
            entities?.edges?.map(item => {
              return { label: item.name, value: item.id };
            }) || [],
        },
        { label: "Description", name: "description", type: "textarea" },
      ];

      return (
        <div className="mt-4 flex flex-col">
          {!makeAvailableInstanceError && (
            <div className="mt-4 flex gap-4">
              {inputs?.map((item, index) => {
                return (
                  <div key={index} className="flex w-full flex-col">
                    {getInput({ ...item, formik })}
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-8 flex">
            <Button
              className="mr-3"
              label={makeAvailableInstanceError ? "Close" : "Cancel"}
              action={makeAvailableInstanceError ? "primary" : "default"}
              onClick={() => {
                closeModal?.();
                formik?.resetForm();
              }}
            />
            {!makeAvailableInstanceError && (
              <Button
                label={makeAvailableInstanceLoading ? "Updating" : "Update"}
                disabled={makeAvailableInstanceLoading}
                onClick={formik?.submitForm}
              />
            )}
          </div>
        </div>
      );
    },
  });

  const setUnconsignModalProps = () => ({
    title: unconsignInstanceError ? "Error" : `Unconsign Instance?`,
    scale: "sm",
    description: unconsignInstanceError
      ? unconsignInstanceError
      : `Are you sure you wish to unconsign this instance"?`,
    closeOnBackdrop: true,
    onClose: () => resetUnconsignInstance(),
    body: ({ closeModal }) => {
      const handleUnconsign = () => {
        unconsignInstance({
          variables: {
            input: { id: instance?.id },
          },
          update: (cache, { data }) => {
            const { unconsignInstance: { success } = {} } = data || {};
            if (success) {
              closeModal();
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={unconsignInstanceError ? "Close" : "Cancel"}
            action={unconsignInstanceError ? "primary" : "default"}
            onClick={() => {
              closeModal?.();
            }}
          />
          {!unconsignInstanceError && (
            <Button
              label={unconsignInstanceLoading ? "Unconsigning" : "Unconsign"}
              disabled={unconsignInstanceLoading}
              onClick={handleUnconsign}
            />
          )}
        </div>
      );
    },
  });

  const setUndoStatusInstanceModalProps = () => ({
    title: undoStatusInstanceError ? "Error" : `Undo Status Change?`,
    scale: "sm",
    description: undoStatusInstanceError
      ? undoStatusInstanceError
      : `Are you sure you wish to undo status change for this instance"?`,
    closeOnBackdrop: true,
    onClose: () => resetUndoStatusInstance(),
    body: ({ closeModal }) => {
      const handleUndo = () => {
        undoStatusInstance({
          variables: {
            input: { id: instance?.id },
          },
          update: (cache, { data }) => {
            const { undoStatusInstance: { success } = {} } = data || {};
            if (success) {
              closeModal();
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={undoStatusInstanceError ? "Close" : "Cancel"}
            action={undoStatusInstanceError ? "primary" : "default"}
            onClick={() => {
              closeModal?.();
            }}
          />
          {!undoStatusInstanceError && (
            <Button
              label={undoStatusInstanceLoading ? "Undoing" : "Undo Status"}
              disabled={undoStatusInstanceLoading}
              onClick={handleUndo}
            />
          )}
        </div>
      );
    },
  });

  const storageEstimateModalProps = {
    closeOnBackdrop: true,
    hideCloseButton: true,
    instance,
    scale: "md",
    body: StorageEstimate,
  };

  const changeEditionModalProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    scale: "md",
    instance,
    body: props => (
      <Suspense fallback={<div>Loading...</div>}>
        <ChangeEditionNumber {...props} />
      </Suspense>
    ),
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state (selection, search,
  // every list/working/image-list id) leaks into actions like Export here
  // too. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: instanceId,
    selectedIds: ids,
    fallbacks: {
      listId,
      imageListId,
      workingListId,
      imageWorkingListId,
      search,
      select,
    },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    imageListId: scopedTargets.imageListId,
    workingListId: scopedTargets.workingListId,
    imageWorkingListId: scopedTargets.imageWorkingListId,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const { hasPermission } = useUser();
  const disableMakeAvailable = !hasPermission("CREATE_RESERVATION");

  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        disabled,
        label: "Export Instances",
        modalProps: {
          body: ExportInstancesBody,
          ...defaultActionProps,
        },
      },
      {
        disabled,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !instance?.id && disabled,
        label: "Export Instance Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Instance",
          ids: instance?.id ? [instance?.id] : ids,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        label: "Add to Offers Select List",
        disabled: !instance?.id && !ids.length,
        modalProps: {
          body: AddToOfferSelectList,
          closeOnBackdrop: false,
          hideCloseButton: true,
          ids: instance?.id ? [instance?.id] : ids,
          listId,
          onClose: () => {
            formik?.resetForm();
          },
          scale: "md",
          search,
          select,
        },
      },
      {
        label: "Add Instance to Select List",
        disabled: !instance?.id,
        modalProps: {
          body: AddInstanceToSelectList,
          ...defaultActionProps,
          instance,
          typeId: 3, // Instance ListType
          title: "Add Instance to Select List",
          description: "Do you wish to add the instance to select list?",
        },
      },
      {
        label: "Add Images to Select List",
        disabled: !instance?.edition?.artwork?.images[0]?.archiveId,
        modalProps: {
          body: AddInstanceImagesToSelectList,
          ...defaultActionProps,
          instance,
          typeId: 7, // Instance_Image ListType
          title: "Add Instance images to Select List",
          description: "Do you wish to add the instance images to select list?",
        },
      },
      {
        label: "Add Primary Image to Select List",
        disabled: !instance?.edition?.artwork?.images[0]?.archiveId,
        modalProps: {
          body: AddInstancePrimaryImageToSelectList,
          ...defaultActionProps,
          instance,
          typeId: 7, // Instance_Image ListType
          title: "Add Instance primary image to Select List",
          description:
            "Do you wish to add the instance primary image to select list?",
        },
      },
      {
        label: "Make Available",
        modalProps: setMakeAvailableModalProps(ids),
        disabled:
          disableMakeAvailable ||
          (!ids.length && !instance?.id) ||
          (instance && instance?.statusId !== 14),
      },
      {
        label: "Copy Description",
        disabled: !(instance?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Description",
          scale: "sm",
          description: "Do you wish to copy instance descriptions?",
          closeOnBackdrop: true,
          ids,
          instance,
        },
      },
      {
        label: "Copy Warehouse Description",
        disabled: !instance?.id,
        modalProps: {
          body: CopyWarehouseDescription,
          title: "Copy Warehouse Description?",
          scale: "sm",
          description:
            "Do you wish to copy the warehouse description from the instance?",
          closeOnBackdrop: true,
          instanceId: instance?.id,
          instance,
        },
      },
      ...(instance?.id
        ? [
          {
            label: "Create Job",
            modalProps: createJobModalProps,
          },
          {
            label: "Delete",
            modalProps: deleteModalProps,
          },
          {
            label: "Return Instance",
            modalProps: setStatusModalProps({
              status: instanceStatus?.find(item => item?.name === "Returned"),
            }),
          },
          {
            label: "Set Status Destroyed",
            modalProps: setStatusModalProps({
              status: instanceStatus?.find(
                item => item?.name === "Destroyed",
              ),
            }),
          },
          {
            label: "Set Status Inactive",
            modalProps: setStatusModalProps({
              status: instanceStatus?.find(item => item?.name === "Inactive"),
            }),
          },
          {
            label: "Set Status Null",
            modalProps: setStatusModalProps({
              status: instanceStatus?.find(item => item?.name === "Null"),
            }),
          },
          {
            label: "Set Status To Be Returned",
            modalProps: setStatusModalProps({
              status: instanceStatus?.find(
                item => item?.name === "To be Returned",
              ),
            }),
          },
          {
            label: "Storage Estimate",
            modalProps: storageEstimateModalProps,
          },
          {
            label: "Unconsign",
            modalProps: setUnconsignModalProps(),
          },
          {
            label: "Undo Status Change",
            modalProps: setUndoStatusInstanceModalProps(),
          },
          {
            label: "Change Edition Number",
            modalProps: changeEditionModalProps,
          },
          {
            label: "Go To Artwork",
            onClick: () =>
              navigate(`/artworks/${instance?.artworkId}/details?status=all`),
          },
        ]
        : []),
    ],
  };

  const searchModalProps = {
    body: InstanceSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  return (
    <div className="flex items-center justify-between border-b px-8 py-5">
      <Breadcrumb {...breadcrumbProps} />
      <div className="flex justify-end gap-4">
        {showInstanceNavigationControls && (
          <div
            className="flex items-center gap-2"
            title={!hasNavigationContext ? NAVIGATION_CONTEXT_TOOLTIP : ""}
          >
            <Button
              action="default"
              disabled={
                navigationLoading ||
                !showInstanceNavigationControls ||
                !hasNavigationContext ||
                !hasPrevious}
              label="Previous"
              onClick={navigateToPreviousInstance}
            />
            <Button
              action="default"
              disabled={
                navigationLoading ||
                !showInstanceNavigationControls ||
                !hasNavigationContext ||
                !hasNext}
              label="Next"
              onClick={navigateToNextInstance}
            />
          </div>
        )}
        <Modal {...searchModalProps}>
          <Button
            label="Instance Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </div>
  );
};

export default PrimaryNav;
